/**
 * CartQueue — single global serializer for ALL Shopify cart-domain requests.
 *
 * Why this exists:
 *   Shopify rate-limits AJAX cart requests. Two cart requests in flight at the
 *   same time return HTTP 429. On every cart mutation Shopify's injected
 *   `shop_events_listener` ALSO auto-fires a background `GET /cart.js`, so each
 *   POST is effectively POST + a hidden GET. With several independent modules
 *   (cart-store.js, cart-api.js, cart-coupon.js, scripts.js, the cart section
 *   inline script) each firing their own requests, these overlap and trigger
 *   429s plus a sluggish cart.
 *
 *   CartQueue guarantees:
 *     • at most ONE cart request is in flight at a time (global mutex)
 *     • a small spacing gap after each WRITE so shop_events_listener's auto-GET
 *       can drain before the next request fires
 *     • exponential-backoff retry on 429
 *
 * Usage:
 *   CartQueue.fetch('/cart.js')                       // GET, queued
 *   CartQueue.fetch('/cart/update.js', { ... })       // POST, queued + spaced
 *   CartQueue.enqueue(fn)                              // run any async fn in the queue
 *
 * CartQueue.fetch() has the same signature/return as window.fetch() (resolves
 * to a Response), so callers can keep using `.then(r => r.json())` etc.
 *
 * Load order: must be loaded (and deferred) FIRST — before cart-store.js,
 * cart-api.js, scripts.js, custom.js. Add it at the top of head-scripts.liquid.
 */
window.CartQueue = (function () {
  'use strict';

  // Minimum gap (ms) enforced AFTER a write completes, before the next queued
  // request starts. Gives shop_events_listener's automatic GET /cart.js time to
  // finish so it does not collide with the next request. Reads have no gap.
  // shop_events_listener fires its auto /cart.js OUTSIDE this queue (it captured
  // the native fetch), so this gap is our only lever to stop that hidden GET
  // from overlapping the next cart request — keep it long enough to clear it.
  var WRITE_SETTLE_MS = 450;

  // 429 backoff schedule (ms). One entry per retry attempt. Longer than a couple
  // of seconds because a 429 here is usually an EDGE bot-protection checkpoint
  // ("Verifying your connection..."), not Shopify's soft cart rate-limit — those
  // challenges clear over several seconds, so we wait them out. Each delay gets
  // ±25% random jitter (see run()) so retries are not perfectly periodic, which
  // itself looks less automated to bot-protection.
  var BACKOFF_MS = [800, 2000];

  // Apply ±25% random jitter to a backoff delay.
  function _jitter(ms) { return Math.round(ms * (0.75 + Math.random() * 0.5)); }

  var _chain = Promise.resolve(); // tail of the serialized queue

  function _sleep(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // A "write" is any non-GET cart request — it needs the settle gap afterwards.
  function _isWrite(init) {
    var method = (init && init.method ? String(init.method) : 'GET').toUpperCase();
    return method !== 'GET' && method !== 'HEAD';
  }

  // Perform a single fetch with 429 exponential-backoff retry.
  // Pass `init.failFastOn429 = true` to skip the backoff loop entirely and
  // return the 429 immediately — used by callers (e.g. the delivery-method
  // switch) that recover by reloading the page, where burning ~15s of retries
  // first is pointless because the edge checkpoint only clears on navigation.
  function _fetchWithRetry(input, init) {
    var failFast = !!(init && init.failFastOn429);
    var attempt = 0;
    function run() {
      return fetch(input, init).then(function (response) {
        if (response.status !== 429) return response;
        if (failFast) {
          console.warn('[CartQueue] 429 — fail-fast (no retry) for', input);
          return response;
        }
        if (attempt >= BACKOFF_MS.length) {
          console.warn('[CartQueue] 429 — retries exhausted for', input);
          return response;
        }
        var wait = _jitter(BACKOFF_MS[attempt++]);
        console.warn('[CartQueue] 429 — retrying in ' + wait + 'ms (' + attempt + '/' + BACKOFF_MS.length + '):', input);
        return _sleep(wait).then(run);
      });
    }
    return run();
  }

  /**
   * Enqueue an arbitrary async task. Tasks run strictly one after another.
   * A rejected task never breaks the chain for later tasks.
   * Returns a Promise resolving to the task's result.
   */
  function enqueue(task, isWrite) {
    var result = _chain.then(function () { return task(); });

    // Advance the chain even if this task throws, and add the settle gap
    // after writes so the next task waits for shop_events_listener.
    _chain = result.then(
      function () { return isWrite ? _sleep(WRITE_SETTLE_MS) : undefined; },
      function () { return isWrite ? _sleep(WRITE_SETTLE_MS) : undefined; }
    );

    return result;
  }

  /**
   * Queued, retrying fetch. Same signature/return as window.fetch().
   * GETs run back-to-back; writes add a settle gap afterwards.
   */
  function queuedFetch(input, init) {
    return enqueue(function () {
      return _fetchWithRetry(input, init);
    }, _isWrite(init));
  }

  return {
    fetch: queuedFetch,
    enqueue: enqueue
  };
})();
